/**
 * The two focused selections of the v4 report — the PA = NO accounts and the
 * write-downs — defined once, so the tab, its badge, the CSV and the shared
 * snapshot all draw the same lines through the same rows.
 */
import type { ClaimRow } from '../types/raketechFinalReportV4.types';
import { paOf } from './raketechAccess';

export const ftdSpan = (row: Pick<ClaimRow, 'monthly'>) => {
  const months = row.monthly.filter((m) => m.ftds > 0).map((m) => m.month).sort();
  return { first: months[0], last: months[months.length - 1], total: row.monthly.reduce((s, m) => s + (m.ftds || 0), 0), months: months.length };
};

/** The PA = NO tab's default selection: classified PA = NO by Raketech, with
 *  an acquisition on its monthly reports in April 2023 or later — a player in
 *  the first month of the period alone does not count. Used for the tab badge
 *  too, so the two cannot disagree. */
export const isPaNoWithPlayers = (row: ClaimRow): boolean =>
  paOf(row) === 'no' && (ftdSpan(row).last ?? '') > '2023-03';

/** Below this a reduction is rounding between two of Raketech's files, not a
 *  write-down; the default keeps the list to what is worth asking about. */
export const WRITE_DOWN_DEFAULT_MIN = 500;

export interface WriteDown {
  /** Raketech's Reported column: what its monthly reports paid out on. */
  reported: number;
  /** Its June 2025 file, Adwin earnings column. */
  june: number;
  /** Its July 2026 report for the reconciled period. */
  july: number;
  /** Its July 2026 report below its monthly reports, December 2024 on. */
  tail: number;
  reportedToJune: number;
  juneToJuly: number;
  writeDown: number;
  /** Their figure for the reconciled period as it stands now. */
  current: number;
  nulled: boolean;
  ownData: boolean;
  pct: number;
}

/** Three reductions Raketech made to its own figure for an account, each
 *  measured on its own pair of columns so none is counted twice. Reported →
 *  June credited and June → July are for the reconciled period (Mar 2023 – Nov
 *  2024); the tail is the months after it, counted on the mark's footing:
 *  above EUR 100 and above 1% of what was invoiced in those months. */
export function writeDownOf(row: ClaimRow): WriteDown {
  const c = (s: string) => row.candidates.find((x) => x.source === s);
  const reported = row.reference.raketechReported || 0;
  const june = c('june_recon')?.amount || 0;
  const july = c('raketech_restated')?.amount || 0;
  const tailRaw = row.tailWrittenDown || 0;
  const tail = tailRaw > 100 && tailRaw > (row.invoicedPostRecon || 0) * 0.01 ? tailRaw : 0;
  const reportedToJune = Math.max(0, reported - june);
  const juneToJuly = row.inJuneRecon && july > 0 ? Math.max(0, june - july) : (row.inJuneRecon && june > 0 && row.offers.length === 0 ? june : 0);
  const writeDown = reportedToJune + juneToJuly + tail;
  const current = row.offers.length > 0 ? july : june;
  const nulled = (reported > 1 && june <= 0) || (june > 1 && row.offers.length > 0 && july <= 0);
  const ownData = row.candidates.some((x) => (x.source === 'synced' || x.source === 'manual_check') && x.available && x.amount > 0);
  const pct = reported > 0 ? writeDown / reported : (june > 0 ? writeDown / june : 0);
  return { reported, june, july, tail, reportedToJune, juneToJuly, writeDown, current, nulled, ownData, pct };
}

/** The Write-downs tab's default selection: no Adwin data to verify against,
 *  reduction at or above the default minimum. */
export const isDefaultWriteDown = (row: ClaimRow): boolean => {
  const w = writeDownOf(row);
  return !w.ownData && w.writeDown >= WRITE_DOWN_DEFAULT_MIN;
};
